import styled from "@emotion/styled";
import {
  Box,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Switch,
  Button,
  Alert,
  Tooltip,
  Typography,
  IconButton,
} from "@mui/material";
import { keyframes } from "@emotion/react";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import { useQuery } from "@tanstack/react-query";
import React, { useState } from "react";
import Carousel from "react-multi-carousel";
import { useOutletContext } from "react-router-dom";
import {
  getWithQueryApiServices,
  putWithQueryApiServices,
} from "../../../apis/api";
import { apiRoutes } from "../../../apis/apiPath";
import { ListBox } from "../../../styles/commonStyles";
import { successToast, errorToast } from "../../../utils/helper";

const responsive = {
  desktop: {
    breakpoint: { max: 3000, min: 1024 },
    items: 4,
    slidesToSlide: 1,
    position: "none",
  },
  tablet: {
    breakpoint: { max: 1024, min: 464 },
    items: 2,
    slidesToSlide: 2,
  },
  mobile: {
    breakpoint: { max: 464, min: 0 },
    items: 1,
    slidesToSlide: 1,
  },
};

const BoxContainer = styled(Box)(({ theme }) => ({
  padding: "1.2%",
  boxSizing: "border-box",
}));

const CompanyBox = styled(Box)(({ theme }) => ({
  height: "600px",
  width: "calc(100% - 24px)",
  backgroundColor: "white",
  borderRadius: 8,
  padding: "12px 0",
  boxSizing: "border-box",
  borderStyle: "solid",
  borderWidth: 1,
  borderColor: "#bdbdbd",
  display: "flex",
  flexDirection: "column",
  margin: "0 12px",
}));

const CompanyTitle = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "center",
  flexDirection: "column",
  alignItems: "center",
  width: "100%",
}));

const HeaderBox = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  borderBottomStyle: "solid",
  borderBottomWidth: 1,
  borderBottomColor: "#bdbdbd",
}));

// ✅ ANIMATIONS & STYLED COMPONENTS FOR SCROLL BUTTONS
const glowPulse = keyframes`
  0% { transform: scale(1); filter: drop-shadow(0 0 2px rgba(0, 163, 255, 0.4)); }
  50% { transform: scale(1.2); filter: drop-shadow(0 0 10px rgba(0, 163, 255, 0.8)); }
  100% { transform: scale(1); filter: drop-shadow(0 0 2px rgba(0, 163, 255, 0.4)); }
`;

const ArrowPane = styled(Box)(({ side }) => ({
  position: "absolute",
  top: 0,
  [side]: 0,
  height: "100%",
  width: "45px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
  zIndex: 10,
  transition: "all 0.3s ease",
  background:
    side === "right"
      ? "linear-gradient(to right, rgba(255,255,255,0) 0%, rgba(114, 133, 179, 0.1) 100%)"
      : "linear-gradient(to left, rgba(255,255,255,0) 0%, rgba(114, 133, 179, 0.1) 100%)",
  "&:hover": {
    background:
      side === "right"
        ? "linear-gradient(to right, rgba(255,255,255,0) 0%, rgba(114, 133, 179, 0.25) 100%)"
        : "linear-gradient(to left, rgba(255,255,255,0) 0%, rgba(114, 133, 179, 0.25) 100%)",
  },
}));

const PulseIcon = styled(Box)({
  color: "#00A3FF", // Bright blue
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  animation: `${glowPulse} 2s infinite ease-in-out`,
  "& svg": {
    fontSize: "40px",
  },
});

const CustomRightArrow = ({ onClick }) => {
  return (
    <ArrowPane
      side="right"
      onClick={() => onClick()}
      onMouseEnter={() => onClick()}
    >
      <PulseIcon>
        <ChevronRightIcon />
      </PulseIcon>
    </ArrowPane>
  );
};

const CustomLeftArrow = ({ onClick }) => {
  return (
    <ArrowPane
      side="left"
      onClick={() => onClick()}
      onMouseEnter={() => onClick()}
    >
      <PulseIcon>
        <ChevronLeftIcon />
      </PulseIcon>
    </ArrowPane>
  );
};

export const AccessList = ({
  userDetails,
  setUserDetails,
  addNewUser,
  setAddnewUser,
  isTenantBlocked = false, // ✅ Add prop
  tenantMessage = "Your tenant is inactive. This action is not allowed.", // ✅ Add prop
  hasPermissionsChanged = false,
  applyPermissionsToCompany,
  currentCompany,
  onSelectPermissionCompany,
  copySessionSource = null,
  onAccessSaved,
  isCurrentUserSysAdmin = false,
  canUpdateUser = false,
  canSeeAllCompanies = false,
  resolvePermission,
  disabled = false,
  isLevelChecked,
  onLevelChange,
  permissionsList = [],
}) => {
  const [lockCategories, setLockCategories] = useState(true);
  const [accessMode, setAccessMode] = useState("categories");
  const { tenantString, fullCompanyList } = useOutletContext();

  const { data: allUsersWithCategory } = useQuery({
    queryKey: ["allUsersWithCategory", tenantString],
    queryFn: () =>
      getWithQueryApiServices(
        apiRoutes.GET_ALL_USER_WITH_CATEGORY,
        `pageNumber=1&selectedCompany=${currentCompany || 1}${tenantString}`,
      ),
  });

  const { data: allUserAccess } = useQuery({
    queryKey: ["allUserAccess", tenantString, currentCompany],
    queryFn: () =>
      getWithQueryApiServices(
        apiRoutes.GET_ALL_ACCESS_OF_USER,
        `selectedCompany=${currentCompany || 1}${tenantString}`,
      ),
  });

  const displayList = React.useMemo(() => {
    const list =
      fullCompanyList?.map((company) => {
        const categoryData = allUsersWithCategory?.data?.find(
          (item) => Number(item.companyID) === Number(company.companyID),
        );
        return {
          ...company,
          categories: categoryData?.categories || [],
        };
      }) || [];

    // fullCompanyList is already limited to companies the current user can
    // access (tenant/global admins see every company). Do not hide companies
    // the edited user is not assigned to yet — that is how access is granted.
    return [...list].sort((a, b) => {
      if (Number(a.companyID) === Number(currentCompany)) return -1;
      if (Number(b.companyID) === Number(currentCompany)) return 1;
      return 0;
    });
  }, [
    fullCompanyList,
    allUsersWithCategory?.data,
    currentCompany,
  ]);

  const enableDisableCompany = (companyID) => {
    // ✅ Block if tenant inactive
    if (isTenantBlocked) {
      errorToast(tenantMessage);
      return;
    }

    let workData = [...userDetails?.accessList];
    const checkExist = workData?.some(
      (access) => Number(access?.companyID) === Number(companyID),
    );

    let newDefaultCompany = userDetails.defaultcompany;
    if (checkExist) {
      workData = workData?.filter(
        (item) => Number(item?.companyID) !== Number(companyID),
      );
      if (Number(userDetails.defaultcompany) === Number(companyID)) {
        newDefaultCompany = null;
      }
    } else {
      const defaultLevels =
        allUserAccess?.data?.filter((p) =>
          String(p).toLowerCase().includes("search"),
        ) || [];
      const levelList =
        defaultLevels.length > 0 ? [...defaultLevels] : ["Search"];

      workData = [
        ...workData,
        { companyID: Number(companyID), levelList, categoryList: [] },
      ];
    }

    setUserDetails({ ...userDetails, accessList: [...workData], defaultcompany: newDefaultCompany });
  };

  const isCategoryLocked = (category, value) => {
    // ✅ Block if tenant inactive
    if (isTenantBlocked) {
      errorToast(tenantMessage);
      return;
    }

    let workData = [...userDetails?.accessList];
    workData = workData?.map((item) => {
      let getCompany = { ...item };
      const origianlList = allUsersWithCategory?.data?.find(
        (data) => Number(data?.companyID) === Number(item?.companyID),
      );
      const isOriginalCategoryExist =
        origianlList?.categories?.includes(category);

      if (isOriginalCategoryExist) {
        const isCategoryExist = getCompany?.categoryList?.some(
          (cat) => cat === category,
        );
        if (value) {
          if (!isCategoryExist) {
            getCompany = {
              ...getCompany,
              categoryList: [...getCompany?.categoryList, category],
            };
          }
        } else {
          if (isCategoryExist) {
            getCompany = {
              ...getCompany,
              categoryList: getCompany?.categoryList?.filter(
                (item) => item !== category,
              ),
            };
          }
        }
      }

      return getCompany;
    });

    setUserDetails({ ...userDetails, accessList: [...workData] });
  };

  const categoryOnchange = (companyID, category, value) => {
    // ✅ Block if tenant inactive
    if (isTenantBlocked) {
      errorToast(tenantMessage);
      return;
    }

    if (lockCategories) {
      return isCategoryLocked(category, value);
    }
    let workData = [...userDetails?.accessList];
    let getCompany = workData?.find(
      (item) => Number(item?.companyID) === Number(companyID),
    );
    const isCategoryExist = getCompany?.categoryList?.some(
      (cat) => cat === category,
    );

    if (isCategoryExist) {
      getCompany = {
        ...getCompany,
        categoryList: getCompany?.categoryList?.filter(
          (item) => item !== category,
        ),
      };
    } else {
      getCompany = {
        ...getCompany,
        categoryList: [...getCompany?.categoryList, category],
      };
    }

    workData = workData?.map((item) => {
      if (Number(item?.companyID) === Number(companyID)) {
        return getCompany;
      }
      return item;
    });

    setUserDetails({ ...userDetails, accessList: [...workData] });
  };

  const isCategoryChecked = (companyID, category) => {
    let workData = [...(userDetails?.accessList || [])];
    let getCompany = workData?.find(
      (item) => Number(item?.companyID) === Number(companyID),
    );
    return getCompany?.categoryList?.some((item) => item === category) || false;
  };

  const isCompanyChecked = (companyID) => {
    return userDetails?.accessList?.some(
      (access) => companyID === access?.companyID,
    );
  };


  const handleSetDefaultCompany = (companyID) => {
    // ✅ Block if tenant inactive
    if (isTenantBlocked) {
      errorToast(tenantMessage);
      return;
    }

    let workData = [...userDetails?.accessList];
    const isCompanyEnabled = workData?.some(
      (access) => Number(access?.companyID) === Number(companyID),
    );

    if (Number(userDetails.defaultcompany) === Number(companyID)) {
      return;
    }

    if (!isCompanyEnabled) {
      const defaultLevels =
        allUserAccess?.data?.filter((p) =>
          String(p).toLowerCase().includes("search"),
        ) || [];
      const levelList =
        defaultLevels.length > 0 ? [...defaultLevels] : ["Search"];
      workData = [
        ...workData,
        { companyID: Number(companyID), levelList, categoryList: [] },
      ];
    }
    setUserDetails({ ...userDetails, defaultcompany: companyID, accessList: [...workData] });
  };

  const handleLockCategoriesChange = () => {
    // ✅ Block if tenant inactive
    if (isTenantBlocked) {
      errorToast(tenantMessage);
      return;
    }

    const newLock = !lockCategories;
    setLockCategories(newLock);

    if (newLock) {
      const isServiceCheckedInAny = userDetails?.accessList?.some((item) =>
        item?.categoryList?.includes("Service")
      );

      if (isServiceCheckedInAny) {
        let workData = [...(userDetails?.accessList || [])];
        workData = workData.map((item) => {
          let getCompany = { ...item };
          const originalList = allUsersWithCategory?.data?.find(
            (data) => Number(data?.companyID) === Number(item?.companyID),
          );
          const isOriginalCategoryExist =
            originalList?.categories?.includes("Service");
          if (isOriginalCategoryExist) {
            const isCategoryExist = getCompany?.categoryList?.some(
              (cat) => cat === "Service",
            );
            if (!isCategoryExist) {
              getCompany = {
                ...getCompany,
                categoryList: [...getCompany?.categoryList, "Service"],
              };
            }
          }
          return getCompany;
        });
        setUserDetails({ ...userDetails, accessList: [...workData] });
      }
    }
  };

  const handleSave = async () => {
    // ✅ Block if tenant inactive
    if (isTenantBlocked) {
      errorToast(tenantMessage);
      return;
    }

    if (!userDetails?.accessList?.length) {
      errorToast("Select at least one company");
      return;
    }

    try {
      const response = await putWithQueryApiServices(
        apiRoutes.UPDATE_USER_BY_ID,
        `selectedCompany=${currentCompany || ""}${tenantString}`,
        userDetails,
      );
      successToast("User access updated successfully");
      onAccessSaved?.(userDetails?.accessList);
    } catch (error) {
      console.error("Failed to update user access", error);
      // ✅ Handle tenant blocked error
      if (error?.response?.data?.code !== "TENANT_NOT_ACTIVE") {
        errorToast("Failed to update user access");
      }
    }
  };

  return (
    <BoxContainer style={{ pointerEvents: !addNewUser ? "none" : "auto" }}>
      {/* ✅ Show warning banner if tenant is inactive */}
      {isTenantBlocked && (
        <Alert severity="warning" sx={{ mb: 2, mx: 3 }}>
          {tenantMessage}
        </Alert>
      )}

      <Box
        display="flex"
        alignItems="flex-start"
        justifyContent="space-between"
        gap={1}
        sx={{
          position: "relative",
          minHeight: 92,
          paddingLeft: "24px",
          paddingRight: "24px",
          paddingBottom: "8px",
        }}
      >
        <Tooltip title={isTenantBlocked ? tenantMessage : accessMode === "pages" ? "Lock Categories applies while Categories is selected" : ""}>
          <FormControlLabel
            sx={{ mt: 2.5, mr: 1, flexShrink: 0 }}
            control={
              <Checkbox
                checked={lockCategories}
                onChange={handleLockCategoriesChange}
                disabled={isTenantBlocked || accessMode === "pages"}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    e.stopPropagation();
                    handleLockCategoriesChange();
                  }
                }}
              />
            }
            label={"Lock Categories"}
          />
        </Tooltip>
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          sx={{
            position: "absolute",
            left: "50%",
            top: 8,
            transform: "translateX(-50%)",
            zIndex: 1,
            px: 1,
          }}
        >
            <Typography
              variant="body2"
              sx={{ fontWeight: 700, color: "#1b79ce", lineHeight: 1.2, mb: 0.25 }}
            >
              Assign
            </Typography>
            <Box display="flex" alignItems="center" gap={0.5}>
              <Typography
                variant="body2"
                sx={{ fontWeight: accessMode === "categories" ? 700 : 500, color: accessMode === "categories" ? "#1b79ce" : "#64748b" }}
              >
                Categories
              </Typography>
              <Switch
                size="small"
                checked={accessMode === "pages"}
                onChange={(e) => setAccessMode(e.target.checked ? "pages" : "categories")}
                disabled={isTenantBlocked}
                inputProps={{ "aria-label": "Switch between categories and access pages" }}
              />
              <Typography
                variant="body2"
                sx={{ fontWeight: accessMode === "pages" ? 700 : 500, color: accessMode === "pages" ? "#1b79ce" : "#64748b" }}
              >
                Access pages
              </Typography>
            </Box>
            <Typography
              variant="caption"
              sx={{ color: "#ed6c02", fontWeight: 600, textAlign: "center", lineHeight: 1.3, mt: 0.25 }}
            >
              {accessMode === "pages"
                ? "To Assign Categories, please toggle to the left"
                : "To Assign Access pages, please toggle to the right."}
            </Typography>
          </Box>

        {/* ✅ Disable Update button if blocked, hide if no update permission */}
        {canUpdateUser && (
          <Tooltip title={isTenantBlocked ? tenantMessage : ""}>
            <span style={{ flexShrink: 0, marginTop: 20 }}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleSave}
                disabled={isTenantBlocked}
              >
                Update
              </Button>
            </span>
          </Tooltip>
        )}
      </Box>

      <div style={{ padding: "0 20px", position: "relative" }}>
        {displayList?.length > 0 ? (
          <Carousel
            responsive={responsive}
            customRightArrow={<CustomRightArrow />}
            customLeftArrow={<CustomLeftArrow />}
            infinite={false}
          >
            {displayList?.map((item, index) => {
              const sourceAccess =
                userDetails?.accessList?.find(
                  (a) => Number(a.companyID) === Number(currentCompany),
                )?.levelList || [];
              const targetAccess =
                userDetails?.accessList?.find(
                  (a) => Number(a.companyID) === Number(item.companyID),
                )?.levelList || [];

              const sourceAccessResolved = resolvePermission
                ? [
                    ...new Set(
                      sourceAccess
                        .map((val) => resolvePermission(val))
                        .filter(Boolean),
                    ),
                  ]
                : [...new Set(sourceAccess)];
              const targetAccessResolved = resolvePermission
                ? [
                    ...new Set(
                      targetAccess
                        .map((val) => resolvePermission(val))
                        .filter(Boolean),
                    ),
                  ]
                : [...new Set(targetAccess)];

              const allowedUI = [
                "edit categories",
                "edit company",
                "edit metadata",
                "edit users",
                "reporting",
                "scan",
                "search",
              ];
              const sourceAccessUI = sourceAccessResolved.filter((val) =>
                allowedUI.includes(val.toLowerCase()),
              );
              const targetAccessUI = targetAccessResolved.filter((val) =>
                allowedUI.includes(val.toLowerCase()),
              );

                              const isAlreadySame =
                sourceAccessUI.length === targetAccessUI.length &&
                sourceAccessUI.every((val) => targetAccessUI.includes(val));
              const copyChecksActive =
                copySessionSource != null &&
                Number(copySessionSource) === Number(currentCompany);

              const isPermissionCompany =
                accessMode === "pages" &&
                Number(item.companyID) === Number(currentCompany);
              const isDefaultCompany =
                Number(item?.companyID) === Number(userDetails?.defaultcompany);

              return (
                <CompanyBox
                  key={item?.companyID}
                  sx={{
                    borderColor: isPermissionCompany ? "#1b79ce" : "#bdbdbd",
                    borderWidth: isPermissionCompany ? 2 : 1,
                    boxShadow: isPermissionCompany
                      ? "0 0 0 2px rgba(27, 121, 206, 0.18)"
                      : "none",
                  }}
                >
                  <CompanyTitle>
                    <Tooltip
                      title={
                        accessMode !== "pages"
                          ? item?.name
                          : isPermissionCompany
                          ? `Copying permissions from ${item?.name}`
                          : `Click to copy permissions from ${item?.name}`
                      }
                      arrow
                      placement="top"
                    >
                      <Typography
                        variant="subtitle2"
                        role="button"
                        tabIndex={0}
                        onClick={() => {
                          if (accessMode === "pages") {
                            onSelectPermissionCompany?.(item?.companyID);
                          }
                        }}
                        onKeyDown={(e) => {
                          if (
                            accessMode === "pages" &&
                            (e.key === "Enter" || e.key === " ")
                          ) {
                            e.preventDefault();
                            onSelectPermissionCompany?.(item?.companyID);
                          }
                        }}
                        sx={{
                          maxWidth: "90%",
                          textAlign: "center",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          cursor: "pointer",
                          color: isPermissionCompany ? "#1b79ce" : "#334155",
                          fontWeight: isPermissionCompany ? 700 : 600,
                          "&:hover": {
                            color: "#1b79ce",
                            textDecoration: "underline",
                          },
                        }}
                      >
                        {item?.name}
                      </Typography>
                    </Tooltip>
                    <HeaderBox>
                      <FormGroup>
                        {/* ✅ Disable Enable/Disable switch if blocked */}
                        <Tooltip title={isTenantBlocked ? tenantMessage : ""}>
                          <FormControlLabel
                            sx={{
                              m: 0,
                              "& .MuiFormControlLabel-label": {
                                fontSize: { xs: "11px", md: "14px" },
                                fontWeight: 500,
                              },
                            }}
                            control={
                              <Switch
                                size="small"
                                checked={isCompanyChecked(item?.companyID)}
                                onChange={() =>
                                  enableDisableCompany(item?.companyID)
                                }
                                name="enableDisable"
                                disabled={isTenantBlocked || !isCurrentUserSysAdmin || disabled}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter") {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    enableDisableCompany(item?.companyID);
                                  }
                                }}
                              />
                            }
                            label={
                              isCompanyChecked(item?.companyID)
                                ? "Enable"
                                : "Disabled"
                            }
                          />
                        </Tooltip>
                      </FormGroup>
                      <FormGroup style={{ padding: "8px 0" }}>
                        {/* Set Default stays on until another company is chosen */}
                        <Tooltip title={isTenantBlocked ? tenantMessage : isDefaultCompany ? "Choose another company to change the default" : ""}>
                          <FormControlLabel
                            sx={{
                              m: 0,
                              "& .MuiFormControlLabel-label": {
                                fontSize: { xs: "11px", md: "14px" },
                                fontWeight: 500,
                              },
                            }}
                            control={
                              <Switch
                                size="small"
                                checked={isDefaultCompany}
                                onChange={() =>
                                  handleSetDefaultCompany(item?.companyID)
                                }
                                name="defaultcompany"
                                disabled={isTenantBlocked || !isCurrentUserSysAdmin || disabled || isDefaultCompany}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter") {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    handleSetDefaultCompany(item?.companyID);
                                  }
                                }}
                              />
                            }
                            label="Set Default"
                          />
                        </Tooltip>
                      </FormGroup>
                    </HeaderBox>
                    {accessMode === "pages" && Number(item.companyID) !== Number(currentCompany) && (
                      <Box
                        sx={{
                          mt: 1,
                          mb: 0.5,
                          display: "flex",
                          justifyContent: "center",
                        }}
                      >
                        <Tooltip
                          title={
                            !canUpdateUser
                              ? "Only admin can change permissions"
                              : !hasPermissionsChanged
                                ? "Change permissions in selected company first"
                                : isAlreadySame
                                  ? "Permissions already match"
                                  : "Apply same permissions as Current Company"
                          }
                        >
                          <span>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  size="small"
                                  checked={copyChecksActive && isAlreadySame}
                                  onChange={(e) => {
                                    if (
                                      e.target.checked &&
                                      applyPermissionsToCompany
                                    ) {
                                      applyPermissionsToCompany(item.companyID);
                                    }
                                  }}
                                  disabled={
                                    !canUpdateUser ||
                                    isTenantBlocked ||
                                    isAlreadySame ||
                                    !hasPermissionsChanged
                                  }
                                  sx={{
                                    p: 0.5,
                                    color: "#cbd5e1",
                                    "&.Mui-checked": { color: "#1b79ce" },
                                  }}
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter") {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      if (
                                        !isAlreadySame &&
                                        applyPermissionsToCompany
                                      ) {
                                        applyPermissionsToCompany(
                                          item.companyID,
                                        );
                                      }
                                    }
                                  }}
                                />
                              }
                              label={
                                <Typography
                                  variant="caption"
                                  sx={{
                                    fontWeight: 600,
                                    color: isAlreadySame
                                      ? "#94a3b8"
                                      : "#475569",
                                    fontSize: "0.75rem",
                                  }}
                                >
                                  Apply same permissions as Current Company
                                </Typography>
                              }
                              sx={{ m: 0 }}
                            />
                          </span>
                        </Tooltip>
                      </Box>
                    )}
                  </CompanyTitle>
                  <div
                    style={{
                      overflowY:
                        accessMode === "pages" || item?.categories?.length > 0
                          ? "auto"
                          : "hidden",
                      flex: 1,
                      pointerEvents:
                        !isCompanyChecked(item?.companyID) || isTenantBlocked
                          ? "none"
                          : "auto",
                      backgroundColor: !isCompanyChecked(item?.companyID)
                        ? "#f0f0f0"
                        : "white",
                      opacity:
                        !isCompanyChecked(item?.companyID) || isTenantBlocked
                          ? "0.6"
                          : "1",
                    }}
                  >
                    {accessMode === "pages" ? (
                      permissionsList.map((perm) => {
                        const isSearch = String(perm).toLowerCase() === "search";
                        const checked = isSearch || isLevelChecked?.(item?.companyID, perm);
                        return (
                          <ListBox key={perm}>
                            <Tooltip title={isSearch ? "Search is included for every user" : isTenantBlocked ? tenantMessage : ""}>
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    checked={!!checked}
                                    onChange={(e) => {
                                      if (isSearch) return;
                                      onLevelChange?.(item?.companyID, perm, e.target.checked);
                                    }}
                                    disabled={isSearch || isTenantBlocked || disabled || !isCompanyChecked(item?.companyID)}
                                  />
                                }
                                label={perm}
                                style={{ paddingLeft: "24px" }}
                              />
                            </Tooltip>
                          </ListBox>
                        );
                      })
                    ) : item?.categories?.length > 0 ? (
                      item?.categories?.sort()?.map((category) => (
                        <ListBox key={category}>
                          {/* ✅ Disable category checkboxes if blocked */}
                          <Tooltip title={isTenantBlocked ? tenantMessage : ""}>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={isCategoryChecked(
                                    item?.companyID,
                                    category,
                                  )}
                                  onChange={(e) =>
                                    categoryOnchange(
                                      item?.companyID,
                                      category,
                                      e.target.checked,
                                    )
                                  }
                                  disabled={isTenantBlocked || disabled}
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter") {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      const newChecked = !isCategoryChecked(
                                        item?.companyID,
                                        category,
                                      );
                                      categoryOnchange(
                                        item?.companyID,
                                        category,
                                        newChecked,
                                      );
                                    }
                                  }}
                                />
                              }
                              label={category}
                              style={{ paddingLeft: "24px" }}
                            />
                          </Tooltip>
                        </ListBox>
                      ))
                    ) : (
                      <Box
                        sx={{
                          height: "100%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          p: 2,
                          textAlign: "center",
                        }}
                      >
                        <Typography
                          variant="body2"
                          color="textSecondary"
                          sx={{ fontStyle: "italic" }}
                        >
                          No categories added to this company yet
                        </Typography>
                      </Box>
                    )}
                  </div>
                </CompanyBox>
              );
            })}
          </Carousel>
        ) : (
          <Box display="flex" justifyContent="center" alignItems="center" p={4}>
            <Typography
              variant="body1"
              color="textSecondary"
              sx={{ fontFamily: "Poppins" }}
            >
              No categories have been created yet. Please create a category
              first to assign permissions.
            </Typography>
          </Box>
        )}
      </div>
    </BoxContainer>
  );
};
